import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Roles, type InternshipProcessWithNames } from '../../Types/interfaces';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { GetWorkLogEntries } from '../../helper/apiHelper';
import { useMemo } from 'react';
import { useHasRole } from '../useHasRole';

export const useReadyToSignProcessesOfTutor = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter((process) =>
      process.processSteps.some(
        (step) =>
          step.stepName === 'SigningInternshipConventionByTutor' &&
          step.stepStatus === 1 &&
          !step.disabled
      )
    );
  }, [processes]);
};

export const useOngoingProcessesOfTutor = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter((process) =>
      process.processSteps.some(
        (step) => step.stepName === 'WorkReportFilling' && step.stepStatus !== 3
      )
    );
  }, [processes]);
};

export const useWaitingForReviewProcesses = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'WorkReportFilling' && step.stepStatus === 3
        ) &&
        process.processSteps.some(
          (step) =>
            step.stepName === 'ValidateWorkReportByTutor' &&
            step.stepStatus === 1
        )
    );
  }, [processes]);
};

export const useReadyToSignWorkReportsOfTutor = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter((process) =>
      process.processSteps.some(
        (step) =>
          step.stepName === 'SigningWorkReportByTutor' &&
          (step.stepStatus === 1 || step.stepStatus === 2)
      )
    );
  }, [processes]);
};

export const useAcceptedProcesses = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'ValidateWorkReportByTutor' &&
            step.stepStatus === 3
        ) &&
        process.processSteps.some(
          (step) =>
            step.stepName === 'CompletingEvaluationReportByOrganization' &&
            step.stepStatus === 1
        )
    );
  }, [processes]);
};

export const useEvaluatedProcesses = (
  processes: Array<InternshipProcessWithNames>
) => {
  const hasRole = useHasRole();

  const filteredProcesses = processes.filter((process) =>
    process.processSteps.some(
      (step) =>
        step.stepName === 'CompletingEvaluationReportByOrganization' &&
        step.stepStatus === 3
    )
  );

  return useQuery<InternshipProcessWithNames[]>({
    // not the whole processes: they come with new document links on every fetch, so they
    // would start a new, empty query after every refetch (e.g. when the window gets focus).
    // The step statuses are in it, so the list still updates when a step changes
    queryKey: [
      ...QUERY_KEYS.EVALUATED_PROCESSES_OF_TUTOR,
      filteredProcesses.map((process) => ({
        id: process.id,
        stepStatuses: process.processSteps.map((step) => step.stepStatus),
      })),
    ],
    queryFn: async () => {
      const workLogPromises = filteredProcesses.map(async (process) => {
        return await GetWorkLogEntries(process.workLogId);
      });

      const workLogStatuses = (await Promise.all(workLogPromises)).map(
        (log) => {
          return log[0].workLogStatus;
        }
      );

      return filteredProcesses.filter((_process, index) => {
        return workLogStatuses[index] !== 1;
      });
    },
    // the last result stays on the screen while it is fetched again, the tabs don't flash empty
    placeholderData: keepPreviousData,
    enabled: processes && processes.length > 0 && hasRole(Roles.Tutor),
  });
};
